/**
 * Error Message Utilities
 *
 * Maps error codes and types to user-friendly messages.
 * Provides consistent error handling across the application.
 *
 * @module utils/errorMessages
 */

/**
 * Error types for categorization
 */
export const ERROR_TYPES = {
  RATE_LIMIT: 'rate_limit',
  AUTH_EXPIRED: 'auth_expired',
  AUTH_INVALID: 'auth_invalid',
  NETWORK: 'network',
  NOT_FOUND: 'not_found',
  FORBIDDEN: 'forbidden',
  SERVER_ERROR: 'server_error',
  VALIDATION: 'validation',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown',
};

/**
 * Error message templates
 */
const ERROR_MESSAGES = {
  [ERROR_TYPES.RATE_LIMIT]: {
    title: 'Rate Limit Reached',
    message: 'GitHub API limit reached. Retrying in {time}...',
    messageNoTime: 'GitHub API limit reached. Please wait before retrying.',
    recoverable: true,
    severity: 'warning',
  },
  [ERROR_TYPES.AUTH_EXPIRED]: {
    title: 'Session Expired',
    message: 'Your session has expired. Please reconnect GitHub.',
    recoverable: true,
    severity: 'warning',
    action: 'reconnect',
  },
  [ERROR_TYPES.AUTH_INVALID]: {
    title: 'Authentication Failed',
    message: 'GitHub authentication is invalid. Please reconnect your account.',
    recoverable: true,
    severity: 'error',
    action: 'reconnect',
  },
  [ERROR_TYPES.NETWORK]: {
    title: 'Connection Failed',
    message: 'Unable to connect. Please check your internet connection.',
    recoverable: true,
    severity: 'error',
    action: 'retry',
  },
  [ERROR_TYPES.NOT_FOUND]: {
    title: 'Not Found',
    message: 'The requested resource was not found or you don\'t have access.',
    recoverable: false,
    severity: 'error',
  },
  [ERROR_TYPES.FORBIDDEN]: {
    title: 'Access Denied',
    message: 'You don\'t have permission to access this resource.',
    recoverable: false,
    severity: 'error',
  },
  [ERROR_TYPES.SERVER_ERROR]: {
    title: 'Server Error',
    message: 'The server encountered an error. Please try again later.',
    recoverable: true,
    severity: 'error',
    action: 'retry',
  },
  [ERROR_TYPES.VALIDATION]: {
    title: 'Invalid Input',
    message: 'Please check your input and try again.',
    recoverable: true,
    severity: 'warning',
  },
  [ERROR_TYPES.TIMEOUT]: {
    title: 'Request Timeout',
    message: 'The request took too long. Please try again.',
    recoverable: true,
    severity: 'warning',
    action: 'retry',
  },
  [ERROR_TYPES.UNKNOWN]: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred. Please try again.',
    recoverable: true,
    severity: 'error',
    action: 'retry',
  },
};

/**
 * Specific error message overrides for known error codes/messages
 */
const SPECIFIC_ERROR_MESSAGES = {
  // GitHub-specific errors
  'bad credentials': {
    type: ERROR_TYPES.AUTH_INVALID,
    message: 'Your GitHub token is invalid or has been revoked.',
  },
  'rate limit exceeded': {
    type: ERROR_TYPES.RATE_LIMIT,
    message: 'GitHub API rate limit exceeded. Please wait before retrying.',
  },
  'not found': {
    type: ERROR_TYPES.NOT_FOUND,
    message: 'Repository not found or you don\'t have access.',
  },
  'repository access blocked': {
    type: ERROR_TYPES.FORBIDDEN,
    message: 'Access to this repository is blocked.',
  },

  // Network errors
  'failed to fetch': {
    type: ERROR_TYPES.NETWORK,
    message: 'Unable to connect to the server. Check your internet connection.',
  },
  'network error': {
    type: ERROR_TYPES.NETWORK,
    message: 'Network connection failed. Please check your internet.',
  },
  'networkerror': {
    type: ERROR_TYPES.NETWORK,
    message: 'Network connection failed. Please check your internet.',
  },

  // Firebase auth errors
  'auth/invalid-credential': {
    type: ERROR_TYPES.AUTH_INVALID,
    message: 'Invalid email or password.',
  },
  'auth/user-not-found': {
    type: ERROR_TYPES.AUTH_INVALID,
    message: 'No account found with this email.',
  },
  'auth/wrong-password': {
    type: ERROR_TYPES.AUTH_INVALID,
    message: 'Incorrect password.',
  },
  'auth/too-many-requests': {
    type: ERROR_TYPES.RATE_LIMIT,
    message: 'Too many failed attempts. Please try again later.',
  },
  'auth/popup-closed-by-user': {
    type: ERROR_TYPES.UNKNOWN,
    message: 'Sign-in was cancelled. Please try again.',
  },
};

/**
 * Get the error type from an error object.
 *
 * @param {Error|Object|string} error - Error to classify
 * @returns {string} Error type from ERROR_TYPES
 */
export const getErrorType = (error) => {
  if (!error) {
    return ERROR_TYPES.UNKNOWN;
  }

  // Get error message and status
  const message = typeof error === 'string'
    ? error.toLowerCase()
    : (error.message || error.error || '').toLowerCase();
  const status = error.status || error.code || error.statusCode;

  // Check for specific known errors first
  for (const [key, config] of Object.entries(SPECIFIC_ERROR_MESSAGES)) {
    if (message.includes(key)) {
      return config.type;
    }
  }

  // Check error code for Firebase errors
  if (error.code) {
    const firebaseError = SPECIFIC_ERROR_MESSAGES[error.code];
    if (firebaseError) {
      return firebaseError.type;
    }
  }

  // Check HTTP status codes
  if (status) {
    if (status === 401) return ERROR_TYPES.AUTH_INVALID;
    if (status === 403) {
      if (message.includes('rate limit')) return ERROR_TYPES.RATE_LIMIT;
      return ERROR_TYPES.FORBIDDEN;
    }
    if (status === 404) return ERROR_TYPES.NOT_FOUND;
    if (status === 408 || status === 504) return ERROR_TYPES.TIMEOUT;
    if (status === 422) return ERROR_TYPES.VALIDATION;
    if (status >= 500) return ERROR_TYPES.SERVER_ERROR;
  }

  // Check for network-related errors
  if (
    error.name === 'TypeError' ||
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('connection')
  ) {
    return ERROR_TYPES.NETWORK;
  }

  // Check for timeout
  if (message.includes('timeout') || error.name === 'TimeoutError') {
    return ERROR_TYPES.TIMEOUT;
  }

  return ERROR_TYPES.UNKNOWN;
};

/**
 * Get a user-friendly error message.
 *
 * @param {Error|Object|string} error - Error to get message for
 * @param {Object} options - Options
 * @param {string} options.resetTime - Time until rate limit resets (for rate limit errors)
 * @param {boolean} options.includeTitle - Include error title in response
 * @returns {string|Object} Error message or object with title and message
 */
export const getErrorMessage = (error, { resetTime, includeTitle = false } = {}) => {
  const errorType = getErrorType(error);
  const template = ERROR_MESSAGES[errorType];

  // Check for specific error message override
  const message = typeof error === 'string' ? error.toLowerCase() : (error.message || '').toLowerCase();
  for (const [key, config] of Object.entries(SPECIFIC_ERROR_MESSAGES)) {
    if (message.includes(key)) {
      if (includeTitle) {
        return {
          title: ERROR_MESSAGES[config.type].title,
          message: config.message,
          type: config.type,
          severity: ERROR_MESSAGES[config.type].severity,
          recoverable: ERROR_MESSAGES[config.type].recoverable,
          action: ERROR_MESSAGES[config.type].action,
        };
      }
      return config.message;
    }
  }

  // Use template message
  let finalMessage = template.message;

  // Handle rate limit time substitution
  if (errorType === ERROR_TYPES.RATE_LIMIT && resetTime) {
    finalMessage = template.message.replace('{time}', resetTime);
  } else if (errorType === ERROR_TYPES.RATE_LIMIT && !resetTime) {
    finalMessage = template.messageNoTime || template.message.replace(' Retrying in {time}...', '.');
  }

  if (includeTitle) {
    return {
      title: template.title,
      message: finalMessage,
      type: errorType,
      severity: template.severity,
      recoverable: template.recoverable,
      action: template.action,
    };
  }

  return finalMessage;
};

/**
 * Get full error info including severity and actions.
 *
 * @param {Error|Object|string} error - Error to analyze
 * @param {Object} options - Options
 * @returns {Object} Full error information
 */
export const getErrorInfo = (error, options = {}) => {
  const errorType = getErrorType(error);
  const template = ERROR_MESSAGES[errorType];
  const messageResult = getErrorMessage(error, { ...options, includeTitle: true });

  return {
    ...messageResult,
    originalError: error,
    technicalMessage: typeof error === 'string' ? error : error.message || error.error || 'Unknown error',
    timestamp: new Date().toISOString(),
  };
};

/**
 * Check if an error is recoverable (can retry).
 *
 * @param {Error|Object|string} error - Error to check
 * @returns {boolean} True if error is recoverable
 */
export const isRecoverableError = (error) => {
  const errorType = getErrorType(error);
  return ERROR_MESSAGES[errorType]?.recoverable || false;
};

/**
 * Get suggested action for an error.
 *
 * @param {Error|Object|string} error - Error to get action for
 * @returns {string|null} Suggested action or null
 */
export const getSuggestedAction = (error) => {
  const errorType = getErrorType(error);
  return ERROR_MESSAGES[errorType]?.action || null;
};

/**
 * Get severity level for an error.
 *
 * @param {Error|Object|string} error - Error to get severity for
 * @returns {string} Severity level: 'error', 'warning', 'info'
 */
export const getErrorSeverity = (error) => {
  const errorType = getErrorType(error);
  return ERROR_MESSAGES[errorType]?.severity || 'error';
};

/**
 * Format an error for display.
 * Combines user-friendly message with optional technical details.
 *
 * @param {Error|Object|string} error - Error to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.showTechnical - Include technical details
 * @returns {Object} Formatted error { title, message, technical, severity, type, action }
 */
export const formatError = (error, { showTechnical = false } = {}) => {
  const info = getErrorInfo(error);

  return {
    title: info.title,
    message: info.message,
    technical: showTechnical ? info.technicalMessage : null,
    severity: info.severity,
    type: info.type,
    action: info.action,
    recoverable: info.recoverable,
  };
};

export default {
  ERROR_TYPES,
  getErrorType,
  getErrorMessage,
  getErrorInfo,
  isRecoverableError,
  getSuggestedAction,
  getErrorSeverity,
  formatError,
};
